import chatLogoIcon from "shared/assets/images/chat-logo-icon.png";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import Markdown from "markdown-to-jsx";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowUpOutlined, MenuOutlined } from "@ant-design/icons";
import {
  sendMessageChat,
  getChats,
  getChatById,
  setFeedbackMessage,
} from "shared/service/StrmService";
import "./Quincy.scss";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { QuincyButtonClickEventName } from "event-tracker/button-click-event-name";
import ChatHistoryMenu from "./ChatHistoryMenu";
import { RouteComponentProps } from "react-router-dom";
import { ChatContextQuestionEnum } from "shared/enums/QuincyChatOriginEnum";
import RainbowEnergyRing from "./ai-thinking/RainbowEnergyRing";
import BlockInType from "./typing/BlockInType";
import { MotionShimmerCard } from "./shimmer-card/ShimmerCard";
import { Variants, motion } from "framer-motion";
import MessageActions from "./message-actions/MessageActions";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import { ReactComponent as BoltIcon } from "shared/assets/images/icon-bolt.svg";
import { Tag } from "antd";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import { useDispatch, useSelector } from "react-redux";
import {
  getQuincySubscriptionUsage,
  SubscriptionState,
} from "explorer/reducers/subscription";

enum ChatPredefinedQuestionEnum {
  AboutMyCareerStage = 0,
  HowCanIGoToNextStage = 1,
  ExplainAboutAdvance = 2,
  Recommendations = 3,
  SocialMediaContentStrategy = 4,
  PlatformFocusStrategy = 5,
  StrategicPartners = 6,
  TimeManagementStrategy = 7,
  PreReleasePreparation = 8,
  CareerDifferentiation = 9,
  CareerStageErrors = 10,
  ReleasePerformanceAnalysis = 11,
}

enum ChatMessageFeedback {
  Like,
  Dislike,
}

interface Message {
  id: string;
  role: string;
  content: string;
  hide: boolean;
  feedback?: ChatMessageFeedback;
  isFromHistory?: boolean;
}

export interface QuincyMenuItem {
  key: string;
  label: string;
  createdOn: string;
  contextQuestion?: ChatContextQuestionEnum;
}

const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      duration: 0.8,
      bounce: 0.3,
    },
  },
};

const headerVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      type: "spring",
      bounce: 0.2,
    },
  },
};

const containerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const chatTextareaVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      duration: 0.6,
      bounce: 0.2,
    },
  },
};

const Quincy: React.FC<RouteComponentProps> = (props) => {
  const { match, history, location } = props;
  const {
    params: { id },
  }: any = match;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentPlan } = useSubscription();
  const { quincyUsage } = useSelector(
    (state: SubscriptionState) => state.subscription
  );
  const lastMessageRef = useRef<null | HTMLDivElement>(null);
  const chatContainerRef = useRef<null | HTMLDivElement>(null);
  const inputTextRef = useRef<HTMLTextAreaElement>(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [completedMessages, setCompletedMessages] = useState<Set<string>>(
    new Set()
  );
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(id);
  const [menuItems, setMenuItems] = useState<QuincyMenuItem[]>([]);
  const [outputTextRealtime, setOutputTextRealtime] = useState<Message | null>(
    null
  );
  const [inputText, setInputText] = useState(
    (location.state as any)?.firstMessage
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [temporaryUserMessages, setTemporaryUserMessages] = useState<Message[]>(
    []
  );
  const [shouldLoadHistory, setShouldLoadHistory] = useState<boolean>(!!id);
  const [loading, setLoading] = useState<boolean>(false);
  const [historyFadeIn, setHistoryFadeIn] = useState<boolean>(!id); // true se não há id (novo chat), false se tem id (vai carregar histórico)
  const [shuffledIndexes, setShuffledIndexes] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);

  const disableGenerate = useMemo(() => {
    return (
      loading || (currentPlan === SubscriptionPlan.START && quincyUsage <= 0)
    );
  }, [loading, currentPlan, quincyUsage]);

  const shouldShowSugestion =
    currentPlan === SubscriptionPlan.START
      ? quincyUsage > 0 &&
        messages.length === 0 &&
        temporaryUserMessages.length === 0
      : messages.length === 0 && temporaryUserMessages.length === 0;

  const receivingAnswear = outputTextRealtime || isTyping;
  const hasChat = menuItems?.length > 0;
  const shouldOpenHistoryChat = chatHistoryOpen || !isMobile;
  const isSystemMessage = (role: string) => role !== "user";

  const sugestionQuestions = (() => {
    const enumValues = Object.values(ChatPredefinedQuestionEnum).filter(
      (v) => typeof v === "number"
    ) as number[];
    enumValues.sort((a, b) => a - b);
    return enumValues.map((val) => {
      const key = ChatPredefinedQuestionEnum[val] as unknown as string;
      return {
        text: t(`pages.Quincy.predefinedQuestions.${key}`),
        enumValue: val as ChatPredefinedQuestionEnum,
      };
    });
  })();

  const getChatHistory = useCallback(async () => {
    setLoading(chatId === null);
    const chats: any = await getChats();
    setMenuItems(
      chats?.response?.map((x: any) => {
        return {
          key: x.id,
          label: x.title,
          createdOn: x.createdOn,
          contextQuestion: x.contextQuestion,
        };
      })
    );
    setLoading(false);
  }, [chatId]);

  const handleAutoScroll = useCallback((instant: boolean = false) => {
    if (chatContainerRef.current) {
      // Usa requestAnimationFrame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: instant ? "auto" : "smooth",
          });
        }
      });
    }
  }, []);

  // Scroll específico para acompanhar a digitação (instantâneo)
  const handleTypingScroll = useCallback(() => {
    if (chatContainerRef.current) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: "auto",
          });
        }
      });
    }
  }, []);

  const prepareForNewQuestion = (question: string) => {
    setSendDisabled(true);
    setInputText("");
    setOutputTextRealtime(null);
    const lastUserMessage = {
      role: "user",
      content: question,
      hide: false,
      id: `temp-user-${Date.now()}`,
    };

    return lastUserMessage;
  };

  const submitQuestion = async (
    question: string,
    predefinedQuestion?: ChatPredefinedQuestionEnum
  ) => {
    if (sendDisabled || question === "" || disableGenerate) return false;
    setSendDisabled(true);
    setIsTyping(true);
    setInputText("");
    inputTextRef.current?.blur();
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_SEARCH, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    let newMessages = [...messages];
    try {
      if (outputTextRealtime) {
        newMessages.push(outputTextRealtime);
      }

      let previousMessages = newMessages.length > 0 ? [...newMessages] : null;
      const userMessage = prepareForNewQuestion(question);
      setTemporaryUserMessages([userMessage]);

      // Scroll imediato para mostrar a mensagem do usuário
      setTimeout(() => {
        handleAutoScroll();
      }, 100);

      const request = {
        previousMessages: previousMessages,
        predefinedQuestion: predefinedQuestion ?? null,
        prompt: question,
        chatId: chatId,
      };

      const answear: any = await sendMessageChat(request);
      const allMessages = answear.response.messages;
      dispatch(getQuincySubscriptionUsage());

      // Preservar a flag isFromHistory das mensagens existentes
      const updatedMessages = allMessages.map((msg: Message) => {
        const existingMessage = messages.find((m) => m.id === msg.id);
        return {
          ...msg,
          isFromHistory: existingMessage?.isFromHistory ?? false,
        };
      });

      setMessages(updatedMessages);
      setTemporaryUserMessages([]);
      setOutputTextRealtime(null);
      setIsTyping(false); // Desativa o indicador de "pensando"
      await getChatHistory();
      setChatId(answear.response.id);

      lastMessageRef?.current?.scrollIntoView();
      setSendDisabled(false);
    } catch (error) {
      console.error("Error during the request:", error);
      setIsTyping(false); // Desativa o indicador mesmo em caso de erro
      setSendDisabled(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      submitQuestion(inputText);
    }
  };

  const updateMessageFeedbackById = (
    id: string,
    feedback?: ChatMessageFeedback
  ) => {
    const newMessages = messages.map((item) => {
      if (item.id === id) {
        item.feedback = feedback;
      }
      return item;
    });

    if (outputTextRealtime?.id === id) {
      setOutputTextRealtime({
        ...outputTextRealtime,
        feedback: feedback,
      });
    } else {
      setMessages(newMessages);
    }
  };

  const onClickNewChat = async () => {
    setChatId(null);
    setMessages([]);
    setTemporaryUserMessages([]);
    setOutputTextRealtime(null);
    setChatHistoryOpen(false);
    setSendDisabled(false);
    setHistoryFadeIn(true); // Novo chat sempre visível
  };

  const onClickMenuItem = async (info: any) => {
    setShouldLoadHistory(true);
    setChatId(info.key);
    setSendDisabled(false);
    history.push(`/quincy/${info.key}`);
  };

  const onClickLikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_LIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Like);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Like);
  };

  const onClickDislikeMessage = async (messageId: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_DISLIKE, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    updateMessageFeedbackById(messageId, ChatMessageFeedback.Dislike);
    await setFeedbackMessage(messageId, ChatMessageFeedback.Dislike);
  };

  const onClickCopyMessage = (text: string) => {
    dataLayerClickedEvent(QuincyButtonClickEventName.QUINCY_COPY, {
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success<string>(t("pages.Quincy.textCopied"));
      })
      .catch((error) => {
        console.error("Falha ao copiar o texto:", error);
      });
  };

  const handleMessageCompleteCallback = useCallback((messageId: string) => {
    setCompletedMessages((prev) => new Set([...prev, messageId]));
  }, []);

  function shuffleArray(arr: number[]) {
    return arr.sort(() => Math.random() - 0.5);
  }

  useEffect(() => {
    const allIndexes = Array.from(
      { length: sugestionQuestions.length },
      (_, i) => i
    );
    setShuffledIndexes(shuffleArray(allIndexes));
    setPage(0);
  }, [sugestionQuestions.length]);

  const handleShowMoreSuggestions = () => {
    const totalPages = Math.ceil(sugestionQuestions.length / 4);
    if (page + 1 < totalPages) {
      setPage(page + 1);
    } else {
      const allIndexes = Array.from(
        { length: sugestionQuestions.length },
        (_, i) => i
      );
      setShuffledIndexes(shuffleArray(allIndexes));
      setPage(0);
    }
  };

  useEffect(() => {
    if (inputTextRef.current) {
      inputTextRef.current.focus();
      inputTextRef.current.setSelectionRange(
        inputTextRef.current.value?.length,
        inputTextRef.current.value?.length
      );
    }
  }, []);

  useEffect(() => {
    getChatHistory();
  }, [getChatHistory]);

  useEffect(() => {
    const getMessages = async () => {
      setLoading(true);
      setHistoryFadeIn(false); // Deixa invisível durante carregamento
      const chatMessages: any = await getChatById(chatId!);
      setChatId(chatId);
      // Marcar mensagens do histórico
      const messagesWithHistoryFlag = (chatMessages?.response ?? []).map(
        (msg: Message) => ({
          ...msg,
          isFromHistory: true,
        })
      );
      setMessages(messagesWithHistoryFlag);
      setTemporaryUserMessages([]);
      setOutputTextRealtime(null);
      setChatHistoryOpen(false);
      setLoading(false);

      // Scroll para o final ANTES de mostrar (sem o usuário ver)
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop =
            chatContainerRef.current.scrollHeight;
        }
        // Ativa o fade-in após o scroll estar posicionado
        setTimeout(() => {
          setHistoryFadeIn(true);
        }, 50);
      }, 100);
    };

    // Só carrega mensagens se chatId mudou E (não há mensagens OU usuário explicitamente quer carregar histórico)
    if (chatId && (messages.length === 0 || shouldLoadHistory)) {
      getMessages();
      setShouldLoadHistory(false);
    }
  }, [chatId, shouldLoadHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Quincy.QUINCY_HOME,
    });
  }, []);

  // Scroll quando começa a digitar (thinking indicator)
  useEffect(() => {
    if (isTyping) {
      setTimeout(() => {
        handleAutoScroll();
      }, 300);
    }
  }, [isTyping, handleAutoScroll]);

  useEffect(() => {
    dispatch(getQuincySubscriptionUsage());
  }, [dispatch]);

  useEffect(() => {
    if (currentPlan === SubscriptionPlan.START && quincyUsage <= 0) {
      console.log("true");
      setSendDisabled(true);
    }
  }, [quincyUsage, currentPlan]);

  return (
    <div
      className={`page-quincy ${isMobile && "mobile"} ${
        shouldOpenHistoryChat && "chat-history-open"
      }`}
    >
      <ChatHistoryMenu
        menuItems={menuItems}
        chatIdSelected={chatId}
        onClickMenuItem={onClickMenuItem}
        onClickNewChat={onClickNewChat}
        loading={loading}
        hide={chatHistoryOpen}
        onClose={() => setChatHistoryOpen(false)}
        hasChat={hasChat}
      />
      <div className="content">
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SubscriptionDefaultCTA />
        </div>
        <motion.div
          className="header"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 className="title" variants={headerVariants}>
            <>
              {t("pages.Quincy.quincy")}{" "}
              {currentPlan === SubscriptionPlan.START && (
                <Tag className="tag-credits">
                  <BoltIcon fill="#f9b3de" />
                  {quincyUsage ? `Credits: ${quincyUsage}` : "Back in 24h"}
                </Tag>
              )}
            </>
          </motion.h1>
          <motion.h3 className="subtitle" variants={headerVariants}>
            {t("pages.Quincy.title")}
          </motion.h3>
        </motion.div>
        <div className={`chat-wrapper ${isMobile && "mobile"}`}>
          <div
            ref={chatContainerRef}
            className={`chat-container scroll-style ${
              !historyFadeIn && messages.length > 0 ? "history-loading" : ""
            }`}
            style={{ marginBottom: isMobile ? 0 : 100 }}
          >
            {[...messages, ...temporaryUserMessages]
              .filter((x) => !x.hide)
              .map((ev) => (
                <motion.div
                  key={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? `${ev.id}-${ev.content}`
                      : ev.id
                  }
                  className={
                    isSystemMessage(ev.role) ? "message-system" : "message-user"
                  }
                  initial={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? { y: 20, opacity: 0 }
                      : false
                  }
                  animate={
                    ev.role === "user" && ev.id.startsWith("temp-")
                      ? { y: 0, opacity: 1 }
                      : false
                  }
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {isSystemMessage(ev.role) ? (
                    <>
                      <BlockInType
                        key={ev.id}
                        fullText={ev.content}
                        onComplete={() => handleMessageCompleteCallback(ev.id)}
                        onTextChange={handleTypingScroll}
                        skipAnimation={ev.isFromHistory === true}
                      />
                      <MessageActions
                        ref={lastMessageRef}
                        message={ev}
                        isMessageComplete={completedMessages.has(ev.id)}
                        onLike={onClickLikeMessage}
                        onDislike={onClickDislikeMessage}
                        onCopy={onClickCopyMessage}
                      />
                    </>
                  ) : (
                    <Markdown>{ev.content}</Markdown>
                  )}
                </motion.div>
              ))}
            {receivingAnswear && (
              <div className={`message-system`}>
                <div className="message-icon"></div>
                <div className="paragraph">
                  {outputTextRealtime ? (
                    <BlockInType
                      key={outputTextRealtime.id}
                      fullText={outputTextRealtime.content}
                      minSpeed={15}
                      maxSpeed={35}
                      onComplete={() => {
                        setSendDisabled(false);
                        handleMessageCompleteCallback(outputTextRealtime.id);
                      }}
                      onTextChange={handleTypingScroll}
                    />
                  ) : isTyping ? (
                    <div className="thinking-indicator-wrapper">
                      <RainbowEnergyRing
                        isActive={true}
                        size={25}
                        showText={true}
                        textPosition="right"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {shouldShowSugestion && (
            <div className="sugestion-wrapper">
              <img
                src={chatLogoIcon}
                alt="chatLogoIcon"
                className="chat-logo-icon"
                style={{ display: isMobile ? "none" : "none" }}
              />

              <div className={`show-more-btn-wrapper ${isMobile && "mobile"}`}>
                <button
                  className="show-more-btn"
                  onClick={handleShowMoreSuggestions}
                >
                  {t("pages.Quincy.showMoreSuggestions")}
                </button>
              </div>
              <div className="container-grid">
                <motion.div
                  className="suggestions-grid"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {shuffledIndexes.slice(page * 4, page * 4 + 4).map((idx) => {
                    const question = sugestionQuestions[idx];
                    return (
                      <MotionShimmerCard
                        key={idx}
                        text={question.text}
                        enumValue={question.enumValue}
                        onClick={(text, enumValue) =>
                          submitQuestion(
                            text || "",
                            enumValue as ChatPredefinedQuestionEnum
                          )
                        }
                        isMobile={isMobile}
                        variant="compact"
                        variants={cardVariants}
                      />
                    );
                  })}
                </motion.div>
              </div>
            </div>
          )}
          <div className={`search-section ${isMobile && "mobile"}`}>
            <div
              className={`button-history-chat ${isMobile && "mobile"}`}
              onClick={() => setChatHistoryOpen(true)}
              style={{
                background: "transparent",
                alignSelf: "flex-start",
                cursor: "pointer",
                display: isMobile ? "block" : "none",
                marginLeft: 24,
                marginBottom: 24,
                marginTop: 16,
              }}
            >
              <MenuOutlined size={24} />{" "}
              <span style={{ marginLeft: 4 }}>Histórico de Mensagens</span>
            </div>

            <motion.div
              className={`chat-textarea-wrapper ${isMobile && "mobile"}`}
              variants={chatTextareaVariants}
              initial="hidden"
              animate="visible"
            >
              <textarea
                id="chat-textarea"
                placeholder={
                  receivingAnswear ? "" : t("pages.Quincy.placeholder")
                }
                ref={inputTextRef}
                onChange={(ev) => setInputText(ev.target.value)}
                value={inputText}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  // 🔧 Verificar também se está desabilitado para envio
                  if (sendDisabled) {
                    inputTextRef.current?.blur();
                    return;
                  }

                  // Lógica original do foco
                  if (inputTextRef.current) {
                    const length = inputTextRef.current.value?.length ?? 0;
                    inputTextRef.current.setSelectionRange(length, length);
                  }
                }}
              />
              <button
                className={`search-btn${
                  (sendDisabled || inputText === "") && "icon-disabled"
                }`}
                onClick={() => submitQuestion(inputText)}
                disabled={sendDisabled || inputText === "" || disableGenerate}
                style={{
                  cursor: disableGenerate ? "not-allowed" : "pointer",
                }}
              >
                <ArrowUpOutlined />
              </button>
            </motion.div>

            <p className={`disclaimer ${isMobile && "mobile"}`}>
              {t("pages.Quincy.disclaimer")}
            </p>
          </div>
        </div>
        {/* Substituindo Form.Item e Input do Ant Design */}
      </div>
    </div>
  );
};

export default Quincy;
